/**
 * RelationshipsTab — Live config edge visualization (DRR-110, Gap #5)
 *
 * Queries forward and reverse typed edges from the backend configEdges/configReverseEdges
 * resolvers. Shows MANDATED_BY, IMPROVES, MITIGATES, REIMBURSES_VIA, REQUIRES_COMPETENCY,
 * EVIDENCED_BY, FULFILLS etc. with perception-safe labels (DRR-114).
 *
 * Falls back to static PRIMITIVE_DOCUMENTATION for primitives without config IDs.
 *
 * DUMB FRONTEND: All edge data from backend Valkey SET indexes.
 */

import { useMemo } from 'react';
import {
  Stack,
  Group,
  Text,
  Badge,
  Paper,
  Alert,
  Loader,
  Center,
  Divider,
  ThemeIcon,
  Anchor,
} from '@mantine/core';
import {
  IconArrowRight,
  IconArrowLeft,
  IconShieldCheck,
  IconTargetArrow,
  IconAlertTriangle,
  IconCoin,
  IconCertificate,
  IconFileCheck,
  IconChecklist,
} from '@tabler/icons-react';
import { gql } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import type { PrimitiveItem } from '@/hooks/usePrimitiveData';
import { Link } from 'react-router-dom';

// ============================================================================
// TYPES
// ============================================================================

interface RelationshipsTabProps {
  entity: PrimitiveItem | null;
  primitiveType: string;
}

interface ConfigEdge {
  sourceId: string;
  targetId: string;
  edgeType: string;
  inverseType: string;
  ring: number;
}

// ============================================================================
// EDGE TYPE DISPLAY CONFIG — perception-safe labels (DRR-114)
// ============================================================================

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const EDGE_DISPLAY: Record<string, { label: string; color: string; icon: React.ComponentType<any> }> = {
  MANDATED_BY:          { label: 'Required by',            color: 'red',    icon: IconShieldCheck },
  IMPROVES:             { label: 'Supports outcome',       color: 'green',  icon: IconTargetArrow },
  MITIGATES:            { label: 'Reduces risk for',       color: 'orange', icon: IconAlertTriangle },
  REIMBURSES_VIA:       { label: 'Billing pathway',        color: 'blue',   icon: IconCoin },
  REQUIRES_COMPETENCY:  { label: 'Requires training',      color: 'violet', icon: IconCertificate },
  EVIDENCED_BY:         { label: 'Documented via',         color: 'indigo', icon: IconFileCheck },
  FULFILLS:             { label: 'Advances goal',          color: 'teal',   icon: IconChecklist },
  DEPENDS_ON:           { label: 'Depends on',             color: 'gray',   icon: IconArrowRight },
  GOVERNED_BY:          { label: 'Governed by',            color: 'red',    icon: IconShieldCheck },
  IMPLEMENTS:           { label: 'Implements',             color: 'cyan',   icon: IconChecklist },
};

const INVERSE_DISPLAY: Record<string, { label: string; color: string }> = {
  MANDATES:             { label: 'Mandates',               color: 'red' },
  IMPROVED_BY:          { label: 'Improved by',            color: 'green' },
  MITIGATED_BY:         { label: 'Mitigated by',           color: 'orange' },
  REIMBURSED_BY:        { label: 'Reimbursed by',          color: 'blue' },
  COMPETENCY_FOR:       { label: 'Competency for',         color: 'violet' },
  EVIDENCE_FOR:         { label: 'Evidence for',           color: 'indigo' },
  FULFILLED_BY:         { label: 'Fulfilled by',           color: 'teal' },
  DEPENDED_ON_BY:       { label: 'Depended on by',         color: 'gray' },
};

// ============================================================================
// QUERIES
// ============================================================================

const CONFIG_EDGES_QUERY = gql`
  query ConfigEdgesForEntity($configId: String!) {
    configEdges(configId: $configId) {
      sourceId
      targetId
      edgeType
      inverseType
      ring
    }
    configReverseEdges(configId: $configId) {
      sourceId
      targetId
      edgeType
      inverseType
      ring
    }
  }
`;

// ============================================================================
// HELPERS
// ============================================================================

function configIdRoute(configId: string): string | null {
  if (configId.startsWith('rs-')) return `/compliance/rulesets`;
  if (configId.startsWith('wf-')) return `/ferp-core/workflows`;
  if (configId.startsWith('tg-')) return null; // task groups don't have a dedicated page yet
  if (configId.startsWith('tag:')) return null; // vocabulary tags
  return null;
}

// ============================================================================
// COMPONENT
// ============================================================================

export function RelationshipsTab({ entity }: Readonly<RelationshipsTabProps>) {
  const entityId = entity?.id as string | undefined;

  const { data, loading, error } = useQuery<{
    configEdges: ConfigEdge[];
    configReverseEdges: ConfigEdge[];
  }>(CONFIG_EDGES_QUERY, {
    variables: { configId: entityId ?? '' },
    skip: !entityId,
    fetchPolicy: 'cache-first',
  });

  const forwardEdges = useMemo(() => data?.configEdges ?? [], [data]);
  const reverseEdges = useMemo(() => data?.configReverseEdges ?? [], [data]);

  // Group forward edges by type
  const forwardGrouped = useMemo(() => {
    const groups: Record<string, ConfigEdge[]> = {};
    for (const edge of forwardEdges) {
      const key = edge.edgeType;
      if (!groups[key]) groups[key] = [];
      groups[key]!.push(edge);
    }
    return groups;
  }, [forwardEdges]);

  // Group reverse edges by inverse type
  const reverseGrouped = useMemo(() => {
    const groups: Record<string, ConfigEdge[]> = {};
    for (const edge of reverseEdges) {
      const key = edge.inverseType;
      if (!groups[key]) groups[key] = [];
      groups[key]!.push(edge);
    }
    return groups;
  }, [reverseEdges]);

  if (!entityId) {
    return (
      <Center py="xl">
        <Text c="dimmed" size="sm">Select an entity to view relationships.</Text>
      </Center>
    );
  }

  if (loading) {
    return (
      <Center py="xl">
        <Stack align="center" gap="sm">
          <Loader size="sm" />
          <Text c="dimmed" size="sm">Loading relationships...</Text>
        </Stack>
      </Center>
    );
  }

  if (error) {
    return <Alert color="red" title="Relationships Unavailable">{error.message}</Alert>;
  }

  if (forwardEdges.length === 0 && reverseEdges.length === 0) {
    return (
      <Center py="xl">
        <Text c="dimmed" size="sm">No typed edges found for this entity.</Text>
      </Center>
    );
  }

  return (
    <Stack gap="md">
      {/* Forward Edges — "This entity..." */}
      {forwardEdges.length > 0 && (
        <>
          <Group gap="xs">
            <IconArrowRight size={14} color="var(--mantine-color-blue-6)" />
            <Text size="sm" fw={600}>Outgoing Relationships</Text>
            <Badge size="xs" variant="outline">{forwardEdges.length}</Badge>
          </Group>

          {Object.entries(forwardGrouped).map(([edgeType, edges]) => {
            const display = EDGE_DISPLAY[edgeType] ?? { label: edgeType, color: 'gray', icon: IconArrowRight };
            const EdgeIcon = display.icon;
            return (
              <Paper key={edgeType} p="sm" withBorder>
                <Group gap="xs" mb="xs">
                  <ThemeIcon size="sm" variant="light" color={display.color}>
                    <EdgeIcon size={12} />
                  </ThemeIcon>
                  <Text size="sm" fw={500} c={display.color}>{display.label}</Text>
                </Group>
                <Group gap={4}>
                  {edges.map((edge) => {
                    const route = configIdRoute(edge.targetId);
                    return route ? (
                      <Badge
                        key={`${edge.edgeType}-${edge.targetId}`}
                        variant="light"
                        size="sm"
                        color={display.color}
                        component={Link}
                        to={route}
                        style={{ cursor: 'pointer', textDecoration: 'none' }}
                      >
                        {edge.targetId}
                      </Badge>
                    ) : (
                      <Badge
                        key={`${edge.edgeType}-${edge.targetId}`}
                        variant="light"
                        size="sm"
                        color={display.color}
                      >
                        {edge.targetId}
                      </Badge>
                    );
                  })}
                </Group>
              </Paper>
            );
          })}
        </>
      )}

      {forwardEdges.length > 0 && reverseEdges.length > 0 && <Divider />}

      {/* Reverse Edges — "Referenced by..." */}
      {reverseEdges.length > 0 && (
        <>
          <Group gap="xs">
            <IconArrowLeft size={14} color="var(--mantine-color-green-6)" />
            <Text size="sm" fw={600}>Incoming Relationships</Text>
            <Badge size="xs" variant="outline">{reverseEdges.length}</Badge>
          </Group>

          {Object.entries(reverseGrouped).map(([inverseType, edges]) => {
            const display = INVERSE_DISPLAY[inverseType] ?? { label: inverseType, color: 'gray' };
            return (
              <Paper key={inverseType} p="sm" withBorder>
                <Group gap="xs" mb="xs">
                  <ThemeIcon size="sm" variant="light" color={display.color}>
                    <IconArrowLeft size={12} />
                  </ThemeIcon>
                  <Text size="sm" fw={500} c={display.color}>{display.label}</Text>
                </Group>
                <Group gap={4}>
                  {edges.map((edge) => {
                    const route = configIdRoute(edge.sourceId);
                    return route ? (
                      <Anchor
                        key={`${edge.inverseType}-${edge.sourceId}`}
                        component={Link}
                        to={route}
                        size="sm"
                        style={{ textDecoration: 'none' }}
                      >
                        <Badge variant="light" size="sm" color={display.color} style={{ cursor: 'pointer' }}>
                          {edge.sourceId}
                        </Badge>
                      </Anchor>
                    ) : (
                      <Badge
                        key={`${edge.inverseType}-${edge.sourceId}`}
                        variant="light"
                        size="sm"
                        color={display.color}
                      >
                        {edge.sourceId}
                      </Badge>
                    );
                  })}
                </Group>
              </Paper>
            );
          })}
        </>
      )}
    </Stack>
  );
}
